import { useEffect, useRef } from "react";
import Button from "../components/Button";
import Badge from "../components/Badge";
import { useMe } from "../hooks/useApi";
import { formatToman, formatNumber, toPersianDigits } from "../utils/format";
import { getPersonType } from "../utils/profile";

interface InvoiceDrawerProps {
  selectedPayment: Record<string, unknown> | null;
  onClose: () => void;
}

function formatDateTime(dateStr: unknown): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr as string);
  const date = d.toLocaleDateString("fa-IR", { year: "numeric", month: "long", day: "numeric" });
  const time = d.toLocaleTimeString("fa-IR", { hour: "2-digit", minute: "2-digit" });
  return `${date} ساعت ${time}`;
}

function transactionTypeLabel(type: unknown): string {
  switch (type) {
    case "subscription_purchase":
      return "خرید اشتراک";
    case "charge_wallet":
      return "شارژ کیف پول";
    default:
      return (type as string) || "—";
  }
}

function statusBadge(status: unknown) {
  switch (status) {
    case "verified":
      return <Badge variant="success">تأیید شده</Badge>;
    case "pending":
      return <Badge variant="warning">در انتظار</Badge>;
    case "failed":
      return <Badge variant="error">ناموفق</Badge>;
    case "expired":
      return <Badge variant="warning">منقضی شده</Badge>;
    default:
      return <Badge variant="info">{(status as string) || "—"}</Badge>;
  }
}

function statusExplanation(status: unknown, statusCode: unknown): string {
  const code = String(statusCode ?? "");
  switch (status) {
    case "verified":
      return code === "101"
        ? "پرداخت با موفقیت انجام شد (این تراکنش پیش‌تر تأیید شده بود)"
        : "پرداخت با موفقیت انجام شد";
    case "pending":
      return "این پرداخت هنوز تکمیل نشده است؛ در صورت عدم تکمیل تا ۳۰ دقیقه، به‌صورت خودکار منقضی می‌شود";
    case "expired":
      return "به دلیل عدم تکمیل در زمان معین، پرداخت منقضی شد";
    case "failed":
      switch (code) {
        case "NOK":
          return "به دلیل انصراف کاربر، پرداخت ناموفق بود";
        case "-51":
          return "پرداخت توسط کاربر تکمیل نشد";
        case "-54":
          return "به دلیل انقضای فرصت پرداخت، تراکنش ناموفق بود";
        case "-50":
          return "مبلغ پرداخت‌شده با مبلغ تراکنش مطابقت ندارد";
        case "-53":
          return "این پرداخت متعلق به فروشنده دیگری است";
        default:
          return "پرداخت ناموفق بود؛ در صورت کسر مبلغ، مبلغ ظرف ۷۲ ساعت بازگشت داده می‌شود";
      }
    default:
      return "";
  }
}

// Status-code → readable Persian label. Codes we don't recognize fall back to
// the raw value so information is never lost, but known codes are always
// displayed as text (e.g. "100" → "موفق", "-51" → "پرداخت توسط کاربر تکمیل نشد").
const STATUS_CODE_LABELS: Record<string, string> = {
  // Successful (verified)
  "100": "موفق",
  "101": "پرداخت قبلاً تأیید شده",
  // Cancelled / failed (ZarinPal + gateway)
  "NOK": "لغو پرداخت توسط کاربر",
  "-49": "عدم اعتبار هویت",
  "-50": "عدم تطابق مبلغ پرداختی",
  "-51": "پرداخت توسط کاربر تکمیل نشد",
  "-52": "خطای غیرمنتظره درگاه",
  "-53": "پرداخت متعلق به فروشنده دیگری است",
  "-54": "انقضای فرصت پرداخت",
  "-55": "موجودی کافی نیست",
  "-56": "کارت در لیست سیاه بانکی",
  "-57": "IP نامعتبر",
  "-58": "تاریخ اعتبار کارت نادرست",
  "-59": "کد امنیتی CVV2 نادرست",
};

function statusCodeLabel(status: unknown, statusCode: unknown): string {
  const code = String(statusCode ?? "").trim();
  if (!code) return "—";
  const label = STATUS_CODE_LABELS[code];
  if (label) return label;
  return code;
}

interface RowProps {
  label: string;
  value: React.ReactNode;
  ltr?: boolean;
  strong?: boolean;
}

function Row({ label, value, ltr, strong }: RowProps) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-small text-text-secondary">{label}</span>
      <span
        className={`font-number ${strong ? "text-body font-semibold text-primary" : "text-body font-medium text-text-main"}`}
        dir={ltr ? "ltr" : undefined}
      >
        {value}
      </span>
    </div>
  );
}

export default function InvoiceDrawer({ selectedPayment, onClose }: InvoiceDrawerProps) {
  const backdropMouseDown = useRef(false);
  const { data: user } = useMe();

  useEffect(() => {
    if (selectedPayment) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [selectedPayment]);

  if (!selectedPayment) return null;

  const p = selectedPayment;
  const amount = Number(p.amount) || 0;
  const tax = Number(p.tax) || 0;
  const credited = amount - tax;

  const isLegal = getPersonType(user) === "legal";
  const fullName = [user?.name, user?.family_name].filter(Boolean).join(" ") || "";
  const nationalCode = (user?.national_code as string) || "";
  const postalCode = (user?.postal_code as string) || "";
  const companyName = (user?.company_name as string) || "";
  const nationalId = (user?.national_id as string) || "";
  const hasCustomerInfo = isLegal
    ? Boolean(companyName || nationalId)
    : Boolean(fullName || nationalCode || postalCode);

  const explanation = statusExplanation(p.status, p.status_code);

  return (
    <div className="print-wrapper fixed inset-0 z-50" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm animate-fade-in no-print"
        onMouseDown={(e) => { backdropMouseDown.current = e.target === e.currentTarget; }}
        onClick={(e) => { if (e.target === e.currentTarget && backdropMouseDown.current) onClose(); backdropMouseDown.current = false; }}
      />

      {/* Drawer panel — slides in from the left, fills half the page width */}
      <aside className="print-panel absolute top-0 bottom-0 left-0 w-full md:w-1/2 bg-surface-base border-r border-line shadow-[0_20px_60px_rgba(0,0,0,0.15)] flex flex-col overflow-hidden animate-drawer-left">
        {/* Header */}
        <div className="no-print bg-surface px-6 pt-5 pb-4 border-b border-line">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-h2 text-text-main">فاکتور پرداخت</h2>
              <p className="font-number text-small text-text-tertiary ltr text-left mt-1 tracking-tight">
                #{String(p.id)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-text-tertiary hover:text-text-main transition-colors p-1 shrink-0"
              aria-label="بستن"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Printable receipt */}
          <div id="receipt-print" className="bg-surface rounded-card shadow-card p-6 max-w-md mx-auto">
            {/* Brand */}
            <div className="text-center mb-4">
              <p className="text-h1 font-bold text-text-main">هوش مصنوعی آشا</p>
              <p className="text-small text-text-tertiary mt-1">فاکتور خرید</p>
            </div>

            {/* Receipt meta */}
            <div className="flex items-center justify-between py-3 mb-2 border-y-2 border-dashed border-line">
              <p className="text-small text-text-tertiary">وضعیت پرداخت</p>
              {statusBadge(p.status)}
            </div>

            {/* Status explanation */}
            {explanation && (
              <p className="text-small text-text-tertiary mb-4 -mt-1">{explanation}</p>
            )}

            {/* Amount breakdown */}
            <div className="space-y-2.5 mb-4">
              <Row label="مبلغ کل (شامل مالیات)" value={`${formatToman(amount)} تومان`} />
              <Row label="مالیات (۱۰٪)" value={`${formatToman(tax)} تومان`} />
              <div className="border-t border-dashed border-line" />
              <Row label="مبلغ واریز شده" value={`${formatToman(credited)} تومان`} strong />
            </div>

            {/* Receipt details */}
            <div className="py-1 border-y-2 border-dashed border-line">
              <Row label="نوع تراکنش" value={transactionTypeLabel(p.transaction_type)} />
              {p.plan_name ? <Row label="پلن اشتراک" value={p.plan_name as string} /> : null}
              {/* {p.duration ? <Row label="مدت اشتراک" value={`${formatNumber(p.duration as number)} ماه`} /> : null} */}
              <Row label="کد وضعیت" value={statusCodeLabel(p.status, p.status_code)} />
              <Row label="کد رهگیری (زرین‌پال)" value={(p.ref_id as string) || "—"} ltr />
              {p.card_pan ? <Row label="شماره کارت" value={p.card_pan as string} ltr /> : null}
              <Row label="درگاه" value={(p.gateway as string) || "—"} ltr />
              <Row label="تاریخ پرداخت" value={formatDateTime(p.created_at)} />
              <Row label="تاریخ تأیید" value={formatDateTime(p.verified_at)} />
            </div>

            {/* Customer info */}
            {hasCustomerInfo && (
              <>
                <p className="text-h3 font-bold text-text-main mt-4 mb-1">اطلاعات خریدار</p>
                <div className="py-1 border-y-2 border-dashed border-line">
                  <Row label="نوع هویت" value={isLegal ? "حقوقی" : "حقیقی"} />
                  {isLegal ? (
                    <>
                      {companyName ? <Row label="نام شرکت" value={companyName} /> : null}
                      {nationalId ? <Row label="شناسه ملی" value={toPersianDigits(nationalId)} ltr /> : null}
                    </>
                  ) : (
                    <>
                      {fullName ? <Row label="نام و نام خانوادگی" value={fullName} /> : null}
                      {nationalCode ? <Row label="کد ملی" value={toPersianDigits(nationalCode)} ltr /> : null}
                      {postalCode ? <Row label="کد پستی" value={toPersianDigits(postalCode)} ltr /> : null}
                    </>
                  )}
                </div>
              </>
            )}

            <p className="text-center text-small text-text-tertiary mt-4">از پرداخت شما سپاسگزاریم</p>
          </div>
        </div>

        {/* Footer actions — only successful payments are printable invoices */}
        {p.status === "verified" && (
          <div className="no-print bg-surface px-6 py-4 border-t border-line">
            <Button className="w-full !py-3" onClick={() => window.print()}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              چاپ فاکتور
            </Button>
          </div>
        )}
      </aside>
    </div>
  );
}
